import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import {
  Area,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  buildComparisonCurve,
  fetchBenchmarkData,
} from "../../util/portfolioBuilderLogic";
import { fmtUSD, loadPortfolioMetrics, minimumCapitalFor } from "../../util/portfolioSnapshot";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.55, ease: "easeOut" } },
};

function downsample(series, target = 360) {
  if (!series?.length || series.length <= target) return series || [];
  const step = Math.max(1, Math.floor(series.length / target));
  const out = [];
  for (let i = 0; i < series.length; i += step) out.push(series[i]);
  const last = series[series.length - 1];
  if (out[out.length - 1] !== last) out.push(last);
  return out;
}

function ChartTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  const row = payload[0]?.payload || {};
  return (
    <div className="rounded-xl border border-white/[0.12] bg-zinc-950/95 px-3 py-2.5 text-xs shadow-2xl">
      <p className="text-zinc-500 mb-1.5">{label}</p>
      <p className="text-brand-light font-semibold">
        ZTA Portfolio{" "}
        <span className="tabular-nums">
          {row.equityPct == null ? "n/a" : `${row.equityPct >= 0 ? "+" : ""}${row.equityPct.toFixed(1)}%`}
        </span>
      </p>
      {row.equity != null && (
        <p className="text-zinc-500 tabular-nums">{fmtUSD(row.equity)}</p>
      )}
      <p className="text-amber-300 font-semibold mt-1.5">
        S&amp;P 500{" "}
        <span className="tabular-nums">
          {row.benchmarkPct == null
            ? "n/a"
            : `${row.benchmarkPct >= 0 ? "+" : ""}${row.benchmarkPct.toFixed(1)}%`}
        </span>
      </p>
      {row.benchmarkEquity != null && (
        <p className="text-zinc-500 tabular-nums">{fmtUSD(row.benchmarkEquity)}</p>
      )}
    </div>
  );
}

function StatCard({ label, value, sub, tone = "neutral", swatch }) {
  const toneClass =
    tone === "positive"
      ? "text-emerald-300 border-emerald-500/25 bg-emerald-500/[0.06]"
      : tone === "negative"
      ? "text-rose-300 border-rose-500/25 bg-rose-500/[0.06]"
      : "text-white border-white/[0.08] bg-white/[0.03]";
  return (
    <div className={`rounded-2xl border px-4 py-4 ${toneClass}`}>
      <p className="text-[11px] uppercase tracking-widest text-zinc-500 font-semibold mb-1.5 flex items-center gap-2">
        {swatch && <span className="w-2 h-2 rounded-full" style={{ background: swatch }} />}
        {label}
      </p>
      <p className="text-2xl font-display tracking-tight tabular-nums">{value}</p>
      {sub && <p className="text-xs text-zinc-500 mt-1">{sub}</p>}
    </div>
  );
}

/**
 * Lazy equity-vs-S&P chart for marketing pages.
 * Uses the same Portfolio Builder comparison math (percent return from
 * minimum capital, SPY total-return series) so numbers stay consistent.
 */
export default function EquityVsSpyChart({ className = "" }) {
  const ref = useRef(null);
  const [shouldLoad, setShouldLoad] = useState(false);
  const [state, setState] = useState({ status: "idle", curve: null, stats: null });

  useEffect(() => {
    if (!ref.current || shouldLoad) return undefined;
    if (!("IntersectionObserver" in window)) {
      setShouldLoad(true);
      return undefined;
    }
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        setShouldLoad(true);
        observer.disconnect();
      },
      { rootMargin: "400px 0px" },
    );
    observer.observe(ref.current);
    return () => observer.disconnect();
  }, [shouldLoad]);

  useEffect(() => {
    if (!shouldLoad) return undefined;
    let alive = true;
    setState({ status: "loading", curve: null, stats: null });

    (async () => {
      try {
        const [{ daily, metrics }, spy] = await Promise.all([
          loadPortfolioMetrics(),
          fetchBenchmarkData("SPY"),
        ]);
        const starting = minimumCapitalFor(metrics);
        const full = buildComparisonCurve(daily, spy, starting);
        const curve = downsample(full);
        if (!curve.length) throw new Error("empty comparison curve");

        const last = curve[curve.length - 1];
        const first = curve[0];
        const algoPct = last.equityPct ?? 0;
        const benchPct = last.benchmarkPct ?? 0;
        const stats = {
          starting,
          firstDate: first.date,
          lastDate: last.date,
          algoEnd: last.equity,
          algoPct,
          algoPnl: last.equity - starting,
          benchEnd: last.benchmarkEquity,
          benchPct,
          benchPnl: last.benchmarkEquity == null ? null : last.benchmarkEquity - starting,
          outperformancePct: algoPct - benchPct,
          outperformanceDollars:
            last.benchmarkEquity == null ? null : last.equity - last.benchmarkEquity,
        };
        if (alive) setState({ status: "ready", curve, stats });
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn("[EquityVsSpyChart]", err);
        if (alive) setState({ status: "error", curve: null, stats: null });
      }
    })();

    return () => {
      alive = false;
    };
  }, [shouldLoad]);

  return (
    <div ref={ref} className={className}>
      {state.status === "ready" && state.curve ? (
        <motion.div
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-40px" }}
          className="rounded-2xl border border-white/[0.08] bg-white/[0.02] p-4 md:p-6"
        >
          <div className="flex flex-wrap items-end justify-between gap-3 mb-5">
            <div>
              <p className="text-xs uppercase tracking-widest text-zinc-500 font-semibold mb-1">
                Equity curve vs S&amp;P 500
              </p>
              <h3 className="font-display text-2xl md:text-3xl text-white tracking-tight">
                Same starting capital. Different path.
              </h3>
            </div>
            <p className="text-xs text-zinc-500 max-w-sm text-right leading-relaxed">
              Percent return from the portfolio&apos;s minimum capital sizing
              ({fmtUSD(state.stats.starting)}). SPY total return with dividends reinvested.
            </p>
          </div>

          <div className="h-[320px] md:h-[380px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={state.curve} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="homeEqGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#6366f1" stopOpacity={0.35} />
                    <stop offset="95%" stopColor="#6366f1" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#1f2937" />
                <XAxis
                  dataKey="date"
                  tick={{ fill: "#71717a", fontSize: 11 }}
                  tickLine={false}
                  axisLine={{ stroke: "#1f2937" }}
                  minTickGap={56}
                  tickFormatter={(d) => {
                    const [y, m] = d.split("-");
                    return `${m}/${y.slice(2)}`;
                  }}
                />
                <YAxis
                  tick={{ fill: "#71717a", fontSize: 11 }}
                  tickLine={false}
                  axisLine={false}
                  tickFormatter={(v) => `${v >= 0 ? "+" : ""}${Math.round(v)}%`}
                  domain={["dataMin", "dataMax"]}
                  width={48}
                />
                <Tooltip content={<ChartTooltip />} />
                <Legend
                  verticalAlign="top"
                  align="right"
                  iconType="circle"
                  wrapperStyle={{ paddingBottom: 8, fontSize: 12, color: "#a1a1aa" }}
                />
                <Area
                  type="monotone"
                  dataKey="equityPct"
                  name="ZTA Portfolio"
                  stroke="#818cf8"
                  strokeWidth={2.25}
                  fill="url(#homeEqGradient)"
                  dot={false}
                  activeDot={{ r: 4, fill: "#818cf8" }}
                />
                <Line
                  type="monotone"
                  dataKey="benchmarkPct"
                  name="S&P 500"
                  stroke="#f59e0b"
                  strokeWidth={1.75}
                  dot={false}
                  activeDot={{ r: 4, fill: "#f59e0b" }}
                  connectNulls
                />
              </ComposedChart>
            </ResponsiveContainer>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-5">
            <StatCard
              label="ZTA Portfolio"
              swatch="#818cf8"
              value={`${state.stats.algoPct >= 0 ? "+" : ""}${state.stats.algoPct.toFixed(1)}%`}
              sub={`${fmtUSD(state.stats.algoEnd)} ending · ${fmtUSD(state.stats.algoPnl)} P&L`}
              tone={state.stats.algoPct >= 0 ? "positive" : "negative"}
            />
            <StatCard
              label="S&P 500 buy & hold"
              swatch="#f59e0b"
              value={`${state.stats.benchPct >= 0 ? "+" : ""}${state.stats.benchPct.toFixed(1)}%`}
              sub={
                state.stats.benchEnd == null
                  ? "Benchmark unavailable"
                  : `${fmtUSD(state.stats.benchEnd)} ending · ${fmtUSD(state.stats.benchPnl)} P&L`
              }
            />
            <StatCard
              label="Outperformance"
              value={
                state.stats.outperformanceDollars == null
                  ? "n/a"
                  : `${state.stats.outperformanceDollars >= 0 ? "+" : ""}${fmtUSD(
                      state.stats.outperformanceDollars,
                    )}`
              }
              sub={`${state.stats.outperformancePct >= 0 ? "+" : ""}${state.stats.outperformancePct.toFixed(1)}% vs S&P`}
              tone={state.stats.outperformancePct >= 0 ? "positive" : "negative"}
            />
          </div>

          <p className="text-[11px] text-zinc-600 mt-4 leading-relaxed">
            Comparison window {state.stats.firstDate} → {state.stats.lastDate}. Historical and
            hypothetical results have limitations and are not indicative of future performance.
            Futures trading involves substantial risk of loss.
          </p>
        </motion.div>
      ) : state.status === "error" ? null : (
        <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] h-[420px] animate-pulse" />
      )}
    </div>
  );
}
